<template>
  <div class="group-single-line-component" :class="{editing:internalState !== states.DEFAULT}">
    <v-row class="ma-0 pl-1 align-center" v-if="showDefaultPanelComputed">
      <v-col class="col-user-img px-0 py-1" cols="auto">
        <img :src="imgSrc" v-if="typeof imgSrc !== 'undefined'" alt=""/>
        <div v-if="typeof imgSrc === 'undefined'" class="default-avatar-background">
          <v-icon class="default-avatar">{{ fallbackIcon }}</v-icon>
        </div>
      </v-col>
      <v-col class="d-flex px-0 py-1" cols="auto">
        <div class="line-text">
          {{ textComputed }}
        </div>
      </v-col>
      <v-col class="d-flex px-0 py-1 flex-grow-1 flex-shrink-1" cols="auto">
        <div class="warning-soft font-weight-medium ma-auto px-1" v-if="showSoftWarningComputed">
          {{ softWarningText }}
        </div>
      </v-col>
      <v-col class="d-flex px-0 py-1 flex-shrink-1 ml-auto" cols="auto" v-if="slots.length !== 0">
        <div class="user-group-wrapper my-auto">
          <template v-for="(slot, index) in slots">
            <div :class="{'user-group-item d-inline-flex align-center px-1': slot.show, 'd-none': !slot.show}" :key="index">
              <slot :name="slot.name"></slot>
            </div>
          </template>
        </div>
      </v-col>
      <v-col class="col-icon-group d-flex justify-end text-right px-0 py-1 ml-auto" cols="auto">
        <div v-if="showAcceptIconComputed" class="icon-button px-1" @click="onAccept">
          <AcceptIcon/>
        </div>
        <div v-if="showDiscardIconComputed" class="icon-button px-1" @click="onDiscard">
          <DiscardIcon/>
        </div>
        <div v-if="showEditIconComputed" class="icon-button px-1" @click="onEdit">
          <Edit2Icon/>
        </div>
        <div v-if="showDeleteIconComputed" class="icon-button px-1"
             @click="onDelete">
          <MinusIcon/>
        </div>
      </v-col>
    </v-row>
    <v-row class="ma-0 pl-1 py-1 create-panel align-center" v-if="showCreatePanelComputed">
      <v-col class="px-0 py-1 col-user-img"></v-col>
      <v-col class="px-0 py-1">
        <TextFieldCustom v-model="textComputed"/>
      </v-col>
      <v-col class="d-flex text-right px-0 py-1" cols="auto">
        <div class="icon-button px-1" @click="onAccept">
          <AcceptIcon/>
        </div>
        <div class="icon-button px-1" @click="onDiscard">
          <DiscardIcon/>
        </div>
      </v-col>
    </v-row>
    <v-row class="warning-hard ma-0" v-if="showHardWarningComputed">
      <v-col class="col-warning-left d-flex flex-grow-0 pa-0">
        <slot name="warning-left"></slot>
      </v-col>
      <v-col class="col-warning-right flex-grow-1 pa-0">
        <slot name="warning-right"></slot>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import AcceptIcon from "@/components/icons/AcceptIcon";
import DiscardIcon from "@/components/icons/DiscardIcon";
import MinusIcon from "@/components/icons/MinusIcon";
import {computed} from "@vue/composition-api";
import Edit2Icon from "@/components/icons/Edit2Icon";
import TextFieldCustom from "@/components/form/TextFieldCustom";

const displayables = {
  WARNING_SOFT: 0,
  WARNING_HARD: 1
}

const states = {
  DEFAULT: 0,
  DELETE: 1,
  EDIT: 2,
  CREATE: 3
}

export default {
  name: "GroupSingleLineComponent",
  components: {TextFieldCustom, Edit2Icon, MinusIcon, DiscardIcon, AcceptIcon},
  props: {
    imgSrc: String,
    fallbackIcon: {
      type: String,
      default: '$person'
    },
    value: {
      type: Object,
      default: () => {return {text:'', state: states.DEFAULT}}
    },
    slots: {
      type: Array,
      default: () => []
    },
    softWarningText: String,
    displayForState: {
      type: Object,
      default: () => {
      }
    },
    showEditIcon: {
      type: Boolean,
      default: true
    },
    showDeleteIcon: {
      type: Boolean,
      default: true
    }
  },
  setup(props, {emit}) {

    const internalState = computed({
      get: () => props.value.state,
      set: (state) => {
        emit('input', {...props.value, state})
      }
    })

    const textComputed = computed({
      get: () => props.value.text,
      set: (text) => {
        emit('input', {...props.value, text})
      }
    })

    const showAcceptIconComputed = computed(() => {
      return internalState.value === states.DELETE || internalState.value === states.EDIT
    })
    const showDiscardIconComputed = computed(() => {
      return internalState.value === states.DELETE || internalState.value === states.EDIT
    })
    const showEditIconComputed = computed(() => {
      return internalState.value === states.DEFAULT && props.showEditIcon
    })
    const showDeleteIconComputed = computed(() => {
      return internalState.value === states.DEFAULT && props.showDeleteIcon
    })

    const showDefaultPanelComputed = computed(() => {
      return internalState.value !== states.CREATE
    })

    const showCreatePanelComputed = computed(() => {
      return internalState.value === states.CREATE
    })

    const showSoftWarningComputed = computed(() => {
      let display = props.displayForState[internalState.value]
      if (typeof display !== 'undefined') {
        for (let i = 0; i < display.length; i++) {
          if (display[i] === displayables.WARNING_SOFT) return true
        }
      }
      return false
    })
    const showHardWarningComputed = computed(() => {
      let display = props.displayForState[internalState.value]
      if (typeof display !== 'undefined') {
        for (let i = 0; i < display.length; i++) {
          if (display[i] === displayables.WARNING_HARD) return true
        }
      }
      return false
    })

    function onAccept() {
      switch (internalState.value) {
        case states.DELETE:
          internalState.value = states.DEFAULT
          emit('deleted')
          break;
        case states.EDIT:
          internalState.value = states.DEFAULT
          emit('edited')
          break;
        case states.CREATE:
          internalState.value = states.DEFAULT
          emit('created')
          break;
      }
    }

    function onDiscard() {
      switch (internalState.value) {
        case states.DELETE:
          internalState.value = states.DEFAULT
          break;
        case states.EDIT:
          internalState.value = states.DEFAULT
          emit('editAborted')
          break;
        case states.CREATE:
          internalState.value = states.DEFAULT
          emit('deleted')
          break;
      }
    }

    function onDelete() {
      internalState.value = states.DELETE
    }

    function onEdit() {
      internalState.value = states.EDIT
    }

    return {
      states,
      internalState,
      textComputed,
      showAcceptIconComputed,
      showDiscardIconComputed,
      showEditIconComputed,
      showDeleteIconComputed,
      showDefaultPanelComputed,
      showCreatePanelComputed,
      showSoftWarningComputed,
      showHardWarningComputed,
      onAccept,
      onDiscard,
      onDelete,
      onEdit
    }
  }
}

export {displayables, states}
</script>

<style scoped lang="scss">
.group-single-line-component {

  + .group-single-line-component {
    border-top: 1px solid map-get($grey, 'lighten-3');
  }

  &.editing {
    border-top: 1px dashed map-get($green, 'darken-1');
    border-bottom: 1px dashed map-get($green, 'darken-1');

    + .group-single-line-component {
      border-top: none;
    }
  }

  .col-user-img {
    margin-top: auto;
    margin-bottom: auto;
    flex: 0 0 56px;
    width: 56px;

    img {
      border-radius: 50%;
      margin-right: 24px;
    }
  }

  .line-text {
    font-size: 16px;
    font-weight: 500;
    line-height: 20px;
    margin-top: auto;
    margin-bottom: auto;
  }

  .default-avatar-background {
    display: flex;
    align-items: center;
    width: 32px;
    height: 32px;
    background-color: map-get($grey, 'lighten-blue-1');
    border-radius: 50%;

    .default-avatar {
      margin: auto;
      height: 50%;
      width: 50%;
    }
  }

  .user-group-wrapper {
    color: map-get($grey, 'lighten-blue-1');
    font-size: 14px;
    line-height: 17px;

    svg {
      width: auto;
      height: 20px;
    }

    .user-group-name {
      padding: 0 $spacer 0 $spacer/2;
    }
  }

  .col-icon-group {
    min-width: 102px;
  }

  .icon-button {
    cursor: pointer;
    height: 22px;
  }

  .warning-soft {
    color: map-get($green, 'darken-1');
    line-height: 22px;
  }

  .warning-hard {
    background-color: map-get($green, 'lighten-5');

    .col-warning-left {
      flex: 0 0 200px;
      width: 200px;
    }
  }
}
</style>

<style lang="scss">
.group-single-line-component {
  .icon-button {
    &:hover {
      svg {
        .a {
          stroke: $icon-hover;
        }
      }
    }
  }
}
</style>
